import { useQuery } from '@apollo/client';
import { DOCUMENT_CONTENT_QUERY } from '../graphql/queries';
import { countChecklist, notePreview } from '../lib/docView';

interface DocRowMetaProps {
  path: string;
  type: 'list' | 'note';
  /** Hide the list progress bar (Home keeps rows quieter than the index). */
  bar?: boolean;
}

interface DocRowContentData {
  documentContent: { path: string; content: string } | null;
}

/**
 * Quiet per-type meta under an index row, parsed from the document's cached
 * content: a "N of M tended" progress line (+ optional thin bar) for lists, a
 * one-line preview for notes. Display-only and fail-quiet — while loading, on
 * error, or when the content has nothing to show, the row just has no meta
 * line. Shared by the Documents index and Home recents.
 */
export function DocRowMeta({ path, type, bar = true }: DocRowMetaProps) {
  const { data } = useQuery<DocRowContentData>(DOCUMENT_CONTENT_QUERY, {
    variables: { path },
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });
  const content = data?.documentContent?.content;
  if (content === undefined || content === null) return null;

  if (type === 'note') {
    const preview = notePreview(content);
    if (!preview) return null;
    return <span className="block truncate text-sm text-[var(--text-tertiary)]">{preview}</span>;
  }

  const { total, done } = countChecklist(content);
  if (total === 0) return null;
  const open = total - done;
  return (
    <span className="block">
      <span className="block text-sm text-[var(--text-tertiary)]">
        {open === 0 ? 'All done' : `${open} left`}
      </span>
      {bar && (
        <span
          className="mt-1 block h-1.5 rounded-full bg-[color-mix(in_oklab,var(--text-tertiary)_15%,transparent)] overflow-hidden"
          role="progressbar"
          aria-valuenow={done}
          aria-valuemin={0}
          aria-valuemax={total}
        >
          <span
            className="block h-full rounded-full bg-[var(--marine-400)] transition-all"
            style={{ width: `${Math.round((done / total) * 100)}%` }}
          />
        </span>
      )}
    </span>
  );
}
